import type { UiBudgetLane, UiBudgetPoint } from '@nightshift/ui-types';

/**
 * Bench model: which lanes exist, which hue they keep, which probe is open.
 * Colour follows the metric so toggling a neighbour cannot repaint the rest.
 */

export interface BudgetLaneView {
  id: string;
  lane: UiBudgetLane;
  hue: string;
}

const KNOWN_HUE: Record<string, string> = {
  load_ms: 'var(--series-1)',
  fps_avg: 'var(--series-3)',
  fps_p95: 'var(--series-2)',
  memory_mb: 'var(--series-4)',
};

const FALLBACK_HUES = ['var(--series-5)', 'var(--series-6)', 'var(--series-1)'] as const;

export function metricHue(metric: string): string {
  if (KNOWN_HUE[metric]) return KNOWN_HUE[metric]!;
  let hash = 0;
  for (let i = 0; i < metric.length; i += 1) hash = (hash + metric.charCodeAt(i) * (i + 1)) % 97;
  return FALLBACK_HUES[hash % FALLBACK_HUES.length]!;
}

/** Every lane the API returned, including a target with no samples. */
export function plottableLanes(lanes: readonly UiBudgetLane[]): BudgetLaneView[] {
  return lanes.map((lane) => ({ id: lane.id, lane, hue: metricHue(lane.metric) }));
}

export function defaultLaneSelection(lanes: readonly BudgetLaneView[]): string[] {
  return lanes.map((lane) => lane.id);
}

export function parseLaneSelection(raw: string | null, lanes: readonly BudgetLaneView[]): string[] | null {
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!Array.isArray(parsed)) return null;
  const known = new Set(lanes.map((lane) => lane.id));
  return parsed.filter((id): id is string => typeof id === 'string' && known.has(id));
}

export function serializeLaneSelection(ids: readonly string[]): string {
  return JSON.stringify(ids);
}

export interface LanePick {
  laneId: string;
  context: 'nightly' | 'mr';
  index: number;
  probe: string | null;
}

export function pointsForPick(lane: UiBudgetLane, pick: LanePick): UiBudgetPoint[] {
  const probe = pick.probe ? lane.probes.find((entry) => entry.name === pick.probe) : null;
  const source = probe ?? lane;
  return pick.context === 'nightly' ? source.nightly : source.mr;
}

export function stepLanePick(lane: UiBudgetLane, pick: LanePick, delta: number): LanePick {
  const points = pointsForPick(lane, pick);
  if (points.length === 0) return pick;
  const index = Math.min(points.length - 1, Math.max(0, pick.index + delta));
  return { ...pick, index };
}
